import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  IconButton,
  LinearProgress,
  List,
  ListItemButton,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import PauseCircleOutlineIcon from '@mui/icons-material/PauseCircleOutline';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import PlaylistPlayIcon from '@mui/icons-material/PlaylistPlay';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import ChecklistIcon from '@mui/icons-material/Checklist';
import AutoAwesomeMotionIcon from '@mui/icons-material/AutoAwesomeMotion';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import { useTranslation } from 'react-i18next';
import aiPlanService, {
  AiPlanDetail,
  AiPlanSummary,
  AiPlanTaskDto,
  AiPlanTaskStatus,
  PLAN_DISABLED_CODE,
} from '../../../services/aiPlanService';

import { PLAN_TEMPLATES, findPlanTemplate } from './planTemplates';
import { isManualCompletionTask } from './planPromptBuilder';

interface AiPlanPanelProps {
  open: boolean;
  onClose: () => void;
  isMobile: boolean;
  /** Chat penceresinin anlık genişliği — panel chat Paper'ının soluna yerleşir. */
  chatWidth: number;
  /** Görev çalıştırma ajan modu gerektirir; kapalıysa "Şimdi yap" pasif + uyarı. */
  agentEnabled: boolean;
  /** Chat şu anda meşgul (gönderim/eylem sürüyor) — yeni görev başlatılamaz. */
  busy: boolean;
  userId: string;
  /** Şu anda bu pencerede çalıştırılan görev (spinner gösterimi). */
  activeTaskId: number | null;
  /** Görev durumu değişince artar — panel açık detayı sunucudan tazeler. */
  refreshKey: number;
  /** Görevi ajan sohbetinde başlatır (continueTask=true → "Devam et" komutu). */
  onRunTask: (plan: AiPlanDetail, task: AiPlanTaskDto, continueTask: boolean) => void;
  /** Kalan tüm görevleri sırayla, eylem onaylarını otomatik vererek çalıştırır. */
  onRunAll: (plan: AiPlanDetail) => void;
}

interface NewTaskDraft {
  title: string;
  promptTemplate: string;
}

interface NewVarDraft {
  key: string;
  value: string;
  /** Şablondan geliyorsa formda gösterilen etiket (anahtar alanı kilitlenir). */
  label?: string;
  /** Şablon değişkeni zorunluysa boş bırakılamaz. */
  required?: boolean;
}

const statusIcon = (task: AiPlanTaskDto, isActive: boolean): React.ReactNode => {
  if (task.status === 'running' && isActive) return <CircularProgress size={16} />;
  switch (task.status) {
    case 'done':
      return <CheckCircleIcon sx={{ fontSize: 18, color: 'success.main' }} />;
    case 'failed':
      return <ErrorOutlineIcon sx={{ fontSize: 18, color: 'error.main' }} />;
    case 'needsReview':
      return <HelpOutlineIcon sx={{ fontSize: 18, color: 'warning.main' }} />;
    case 'running':
      // Bu pencerede aktif olmayan running = yarım kalmış görev
      return <PauseCircleOutlineIcon sx={{ fontSize: 18, color: 'warning.main' }} />;
    default:
      return <RadioButtonUncheckedIcon sx={{ fontSize: 18, color: 'text.disabled' }} />;
  }
};

/**
 * Panelin varsayılan genişliği — eski 320px'in 1/3 fazlası. Sol kenardan
 * sürüklenerek genişletilebilir (chat penceresiyle aynı desen); varsayılan
 * genişlik alt sınırdır.
 */
const PANEL_DEFAULT_WIDTH = 427;

/**
 * Plan Modu paneli — chat penceresinin sol kenarına açılan görev listesi.
 * Chat Paper'ının KARDEŞİ olarak ayrı sabit bir Paper'dır (chat'in sol kenarındaki
 * resize tutamacıyla çakışmaması için); mobilde tam ekran Dialog olur.
 */
const AiPlanPanel: React.FC<AiPlanPanelProps> = ({
  open,
  onClose,
  isMobile,
  chatWidth,
  agentEnabled,
  busy,
  userId,
  activeTaskId,
  refreshKey,
  onRunTask,
  onRunAll,
}) => {
  const { t } = useTranslation('common');

  const [view, setView] = useState<'list' | 'detail' | 'new'>('list');
  const [plans, setPlans] = useState<AiPlanSummary[]>([]);
  const [plan, setPlan] = useState<AiPlanDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [planDisabled, setPlanDisabled] = useState(false);

  /** Elle görev durumu değiştirme menüsü */
  const [taskMenu, setTaskMenu] = useState<{ anchor: HTMLElement; taskId: number } | null>(null);

  /** "Tümünü yap" onay modalı (window.confirm yerine uygulama içi dialog) */
  const [runAllConfirmOpen, setRunAllConfirmOpen] = useState(false);

  /** Plan silme onay modalı (window.confirm yerine uygulama içi dialog) */
  const [deleteConfirmId, setDeleteConfirmId] = useState<number | null>(null);

  // Sol kenardan sürükleyerek genişletme (masaüstü) — AiChatWindow ile aynı desen.
  const [panelWidth, setPanelWidth] = useState(PANEL_DEFAULT_WIDTH);
  const [resizing, setResizing] = useState(false);

  const handleResizeStart = useCallback(
    (e: React.PointerEvent) => {
      if (isMobile) return;
      e.preventDefault();
      setResizing(true);
      const startX = e.clientX;
      const startWidth = panelWidth;
      // Panelin sağ kenarı sabit (chat'in solunda) — sola doğru, chat + kenar payları
      // düşüldükten sonra kalan alana kadar genişleyebilir.
      const maxWidth = Math.max(
        PANEL_DEFAULT_WIDTH,
        Math.min(900, window.innerWidth - chatWidth - 72)
      );

      const onMove = (ev: PointerEvent) => {
        // Sola sürükleyince (clientX küçülür) panel genişler
        const next = startWidth + (startX - ev.clientX);
        setPanelWidth(Math.min(maxWidth, Math.max(PANEL_DEFAULT_WIDTH, next)));
      };
      const onUp = () => {
        setResizing(false);
        window.removeEventListener('pointermove', onMove);
        window.removeEventListener('pointerup', onUp);
      };
      window.addEventListener('pointermove', onMove);
      window.addEventListener('pointerup', onUp);
    },
    [isMobile, panelWidth, chatWidth]
  );

  // Yeni plan formu
  const [newTitle, setNewTitle] = useState('');
  const [newVars, setNewVars] = useState<NewVarDraft[]>([]);
  const [newTasks, setNewTasks] = useState<NewTaskDraft[]>([{ title: '', promptTemplate: '' }]);
  /** Form hazır şablondan doldurulduysa şablon kimliği (backend'e TemplateKey olarak gider). */
  const [newTemplateKey, setNewTemplateKey] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [templateMenuAnchor, setTemplateMenuAnchor] = useState<HTMLElement | null>(null);

  const handleError = useCallback(
    (err: any) => {
      if (err?.code === PLAN_DISABLED_CODE) {
        setPlanDisabled(true);
        setError(null);
      } else {
        setError(err?.message || t('aiChat.planLoadError'));
      }
    },
    [t]
  );

  const loadPlans = useCallback(
    async (silent = false) => {
      if (!userId) return;
      if (!silent) setLoading(true);
      setError(null);
      try {
        setPlans(await aiPlanService.getPlans(userId));
        setPlanDisabled(false);
      } catch (err: any) {
        handleError(err);
      } finally {
        if (!silent) setLoading(false);
      }
    },
    [userId, handleError]
  );

  const loadPlanDetail = useCallback(
    async (planId: number, showSpinner: boolean) => {
      if (!userId) return;
      if (showSpinner) setLoading(true);
      try {
        setPlan(await aiPlanService.getPlan(planId, userId));
        setError(null);
      } catch (err: any) {
        handleError(err);
      } finally {
        if (showSpinner) setLoading(false);
      }
    },
    [userId, handleError]
  );

  // Panel açılınca liste yüklenir
  useEffect(() => {
    if (open && view === 'list') loadPlans();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, view]);

  // Plan verisi değişince (refreshKey artar) AÇIK GÖRÜNÜM sessizce tazelenir:
  // detay açıksa detay, liste açıksa liste — AI chat'ten plan adı değiştirme /
  // yeni plan oluşturma gibi değişiklikler panele girip çıkmadan yansır.
  useEffect(() => {
    if (!open) return;
    if (view === 'detail' && plan) loadPlanDetail(plan.id, false);
    else if (view === 'list') loadPlans(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshKey]);

  const openDetail = (planId: number) => {
    setView('detail');
    setPlan(null);
    loadPlanDetail(planId, true);
  };

  /** Silme butonu: onay modalını açar (chrome confirm yerine uygulama içi dialog). */
  const handleDeletePlan = (planId: number) => setDeleteConfirmId(planId);

  /** Modaldan onaylanan silme. */
  const confirmDeletePlan = async () => {
    const planId = deleteConfirmId;
    setDeleteConfirmId(null);
    if (planId === null) return;
    try {
      await aiPlanService.deletePlan(planId, userId);
      setView('list');
      setPlan(null);
      loadPlans();
    } catch (err: any) {
      handleError(err);
    }
  };

  // ── Dışa / içe aktarma (imzalı .gtsplan.json dosyası) ──
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [importing, setImporting] = useState(false);

  /** Dışa aktarma adı modalı: kullanıcı hem plan adını hem dosya adını seçer. */
  const [exportDialog, setExportDialog] = useState<{ planId: number; title: string } | null>(null);

  /** Dışa aktar butonu: ad seçme modalını açar (mevcut plan adı öntanımlı). */
  const handleExportPlan = (planId: number, title: string) => setExportDialog({ planId, title });

  /** Planı seçilen adla imzalı JSON dosyası olarak indirir (imza sunucuda o ada göre üretilir). */
  const confirmExportPlan = async () => {
    const dialog = exportDialog;
    setExportDialog(null);
    if (!dialog) return;
    const name = dialog.title.trim();
    try {
      const data = await aiPlanService.exportPlan(dialog.planId, userId, name || undefined);
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      // Dosya adı: seçilen addan güvenli karakterler (TR harfleri korunur)
      a.download = `${(name || 'plan').replace(/[^\p{L}\p{N} _-]/gu, '').trim() || 'plan'}.gtsplan.json`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err: any) {
      handleError(err);
    }
  };

  /** Seçilen dosyayı içe aktarır; imza doğrulanamazsa dosya reddedilir. */
  const handleImportFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // aynı dosya tekrar seçilebilsin
    if (!file || importing) return;
    setImporting(true);
    setError(null);
    try {
      let parsed: any;
      try {
        parsed = JSON.parse(await file.text());
      } catch {
        setError(t('aiChat.planImportInvalid'));
        return;
      }
      const created = await aiPlanService.importPlan(parsed, userId);
      setPlan(created);
      setView('detail');
    } catch (err: any) {
      if (err?.code === 'INVALID_PLAN_FILE') setError(t('aiChat.planImportInvalid'));
      else handleError(err);
    } finally {
      setImporting(false);
    }
  };

  const handleSetTaskStatus = async (taskId: number, status: AiPlanTaskStatus) => {
    setTaskMenu(null);
    try {
      await aiPlanService.updateTask(taskId, userId, status);
      if (plan) loadPlanDetail(plan.id, false);
    } catch (err: any) {
      handleError(err);
    }
  };

  const resetNewForm = () => {
    setNewTitle('');
    setNewVars([]);
    setNewTasks([{ title: '', promptTemplate: '' }]);
    setNewTemplateKey(null);
  };

  const applyTemplate = (templateKey: string) => {
    setTemplateMenuAnchor(null);
    const template = findPlanTemplate(templateKey);
    if (!template) return;
    setNewTitle(template.title);
    setNewVars(
      template.variables.map((v) => ({ key: v.key, value: '', label: v.label, required: v.required }))
    );
    setNewTasks(template.tasks.map((task) => ({ ...task })));
    setNewTemplateKey(template.key);
    setView('new');
  };

  const canCreate =
    newTitle.trim().length > 0 &&
    newTasks.length > 0 &&
    newTasks.every((task) => task.title.trim() && task.promptTemplate.trim()) &&
    // Şablonun zorunlu değişkenleri doldurulmadan plan oluşturulamaz
    newVars.every((v) => !v.required || v.value.trim().length > 0);

  const handleCreate = async () => {
    if (!canCreate || creating) return;
    setCreating(true);
    try {
      const contextVars: Record<string, string> = {};
      newVars.forEach((v) => {
        if (v.key.trim()) contextVars[v.key.trim()] = v.value.trim();
      });
      const created = await aiPlanService.createPlan(
        {
          title: newTitle.trim(),
          templateKey: newTemplateKey,
          contextVars,
          tasks: newTasks.map((task, i) => ({
            title: task.title.trim(),
            promptTemplate: task.promptTemplate.trim(),
            sortOrder: i,
          })),
        },
        userId
      );
      resetNewForm();
      setPlan(created);
      setView('detail');
    } catch (err: any) {
      handleError(err);
    } finally {
      setCreating(false);
    }
  };

  const moveTask = (index: number, delta: number) => {
    setNewTasks((prev) => {
      const next = [...prev];
      const target = index + delta;
      if (target < 0 || target >= next.length) return prev;
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });
  };

  if (!open) return null;

  const orderedTasks = plan
    ? [...plan.tasks].sort((a, b) => a.sortOrder - b.sortOrder || a.id - b.id)
    : [];

  /** Önceki görevlerin tamamı done mı? (sıralı çalıştırma kuralı) */
  const isPrevDone = (index: number) => orderedTasks.slice(0, index).every((task) => task.status === 'done');

  const renderTaskRow = (task: AiPlanTaskDto, index: number) => {
    const isActive = activeTaskId === task.id;
    const runnable = agentEnabled && !busy && activeTaskId === null;
    // running ama bu pencerede aktif değil = yarım kalmış (pencere kapatılmış vb.)
    const interrupted = task.status === 'running' && !isActive;

    let actionButton: React.ReactNode = null;
    if (task.status === 'pending') {
      const prevDone = isPrevDone(index);
      actionButton = (
        <Tooltip
          title={
            !agentEnabled
              ? t('aiChat.planAgentRequired')
              : !prevDone
                ? t('aiChat.planPrevRequired')
                : ''
          }
        >
          <span>
            <Button
              size="small"
              variant="contained"
              startIcon={<PlayArrowIcon sx={{ fontSize: 14 }} />}
              disabled={!runnable || !prevDone}
              onClick={() => plan && onRunTask(plan, task, false)}
              sx={{ fontSize: 11, py: 0.25, px: 1, minWidth: 0, whiteSpace: 'nowrap' }}
            >
              {t('aiChat.planRunNow')}
            </Button>
          </span>
        </Tooltip>
      );
    } else if (task.status === 'failed' || interrupted) {
      actionButton = (
        <Tooltip title={!agentEnabled ? t('aiChat.planAgentRequired') : ''}>
          <span>
            <Button
              size="small"
              variant="outlined"
              color="warning"
              startIcon={<PlayArrowIcon sx={{ fontSize: 14 }} />}
              disabled={!runnable}
              onClick={() => plan && onRunTask(plan, task, true)}
              sx={{ fontSize: 11, py: 0.25, px: 1, minWidth: 0, whiteSpace: 'nowrap' }}
            >
              {t('aiChat.planContinue')}
            </Button>
          </span>
        </Tooltip>
      );
    } else if (task.status === 'needsReview') {
      // Elle tamamlanan görevlerde ([ELLE-TAMAMLANIR]) yalnızca "Tamamlandı işaretle";
      // durdurma/iptal sonucu needsReview kalan normal görevler "Devam et" ile sürdürülebilir.
      actionButton = (
        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
          {!isManualCompletionTask(task) && (
            <Tooltip title={!agentEnabled ? t('aiChat.planAgentRequired') : ''}>
              <span>
                <Button
                  size="small"
                  variant="outlined"
                  color="warning"
                  startIcon={<PlayArrowIcon sx={{ fontSize: 14 }} />}
                  disabled={!runnable}
                  onClick={() => plan && onRunTask(plan, task, true)}
                  sx={{ fontSize: 11, py: 0.25, px: 1, minWidth: 0, whiteSpace: 'nowrap' }}
                >
                  {t('aiChat.planContinue')}
                </Button>
              </span>
            </Tooltip>
          )}
          <Button
            size="small"
            variant="outlined"
            color="success"
            startIcon={<CheckCircleIcon sx={{ fontSize: 14 }} />}
            onClick={() => handleSetTaskStatus(task.id, 'done')}
            sx={{ fontSize: 11, py: 0.25, px: 1, minWidth: 0, whiteSpace: 'nowrap' }}
          >
            {t('aiChat.planMarkDone')}
          </Button>
        </Box>
      );
    }

    return (
      <Box
        key={task.id}
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 0.75,
          px: 1,
          py: 0.75,
          borderBottom: '1px solid',
          borderColor: 'divider',
          bgcolor: isActive ? 'action.hover' : undefined,
        }}
      >
        <Box sx={{ mt: 0.25, flexShrink: 0 }}>{statusIcon(task, isActive)}</Box>
        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          <Typography variant="body2" sx={{ fontWeight: 500, wordBreak: 'break-word' }}>
            {index + 1}. {task.title}
          </Typography>
          {isActive && (
            <Typography variant="caption" color="primary">
              {t('aiChat.planTaskRunning')}
            </Typography>
          )}
          {interrupted && (
            <Typography variant="caption" color="warning.main">
              {t('aiChat.planTaskInterrupted')}
            </Typography>
          )}
          {task.status === 'done' && task.resultSummary && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
              }}
            >
              {task.resultSummary}
            </Typography>
          )}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, flexShrink: 0 }}>
          {actionButton}
          <IconButton
            size="small"
            onClick={(e) => setTaskMenu({ anchor: e.currentTarget, taskId: task.id })}
          >
            <MoreVertIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Box>
      </Box>
    );
  };

  const content = (
    <>
      {/* Panel başlığı */}
      <Box
        sx={{
          px: 1.5,
          py: 1.25,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          bgcolor: 'primary.main',
          color: 'primary.contrastText',
          flexShrink: 0,
        }}
      >
        {view !== 'list' ? (
          <IconButton
            size="small"
            onClick={() => {
              setView('list');
              setPlan(null);
            }}
            sx={{ color: 'inherit' }}
          >
            <ArrowBackIcon fontSize="small" />
          </IconButton>
        ) : (
          <ChecklistIcon fontSize="small" />
        )}
        <Typography variant="subtitle2" fontWeight={600} noWrap sx={{ flexGrow: 1 }}>
          {view === 'new'
            ? t('aiChat.planNew')
            : view === 'detail'
              ? plan?.title || t('aiChat.planPanelTitle')
              : t('aiChat.planPanelTitle')}
        </Typography>
        {view === 'detail' && plan && (
          <>
            <Tooltip title={t('aiChat.planExport')}>
              <IconButton
                size="small"
                onClick={() => handleExportPlan(plan.id, plan.title)}
                sx={{ color: 'inherit' }}
              >
                <FileDownloadIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title={t('aiChat.planDelete')}>
              <IconButton size="small" onClick={() => handleDeletePlan(plan.id)} sx={{ color: 'inherit' }}>
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </>
        )}
        <IconButton size="small" onClick={onClose} sx={{ color: 'inherit' }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      <Box sx={{ flexGrow: 1, overflow: 'auto', bgcolor: 'grey.50' }}>
        {error && (
          <Alert severity="warning" sx={{ m: 1, fontSize: 12 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {planDisabled ? (
          <Alert severity="info" sx={{ m: 1, fontSize: 12 }}>
            {t('aiChat.planDisabledMsg')}
          </Alert>
        ) : loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={22} />
          </Box>
        ) : view === 'list' ? (
          /* ------- Plan listesi ------- */
          <>
            {!agentEnabled && (
              <Alert severity="info" sx={{ m: 1, fontSize: 12 }}>
                {t('aiChat.planAgentRequired')}
              </Alert>
            )}
            <Box sx={{ display: 'flex', gap: 1, p: 1 }}>
              <Button
                size="small"
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => {
                  resetNewForm();
                  setView('new');
                }}
                sx={{ fontSize: 12 }}
              >
                {t('aiChat.planNew')}
              </Button>
              <Tooltip title={PLAN_TEMPLATES.length === 0 ? t('aiChat.planTemplateSoon') : ''}>
                <span>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<AutoAwesomeMotionIcon />}
                    disabled={PLAN_TEMPLATES.length === 0}
                    onClick={(e) => setTemplateMenuAnchor(e.currentTarget)}
                    sx={{ fontSize: 12 }}
                  >
                    {t('aiChat.planTemplate')}
                  </Button>
                </span>
              </Tooltip>
              <Tooltip title={t('aiChat.planImportHint')}>
                <span>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={importing ? <CircularProgress size={14} /> : <FileUploadIcon />}
                    disabled={importing}
                    onClick={() => fileInputRef.current?.click()}
                    sx={{ fontSize: 12 }}
                  >
                    {t('aiChat.planImport')}
                  </Button>
                </span>
              </Tooltip>
              <input
                ref={fileInputRef}
                type="file"
                accept=".json,application/json"
                hidden
                onChange={handleImportFile}
              />
              <Menu
                anchorEl={templateMenuAnchor}
                open={Boolean(templateMenuAnchor)}
                onClose={() => setTemplateMenuAnchor(null)}
                sx={{ zIndex: 2300 }}
              >
                {PLAN_TEMPLATES.map((tpl) => (
                  <MenuItem key={tpl.key} onClick={() => applyTemplate(tpl.key)} sx={{ maxWidth: 300 }}>
                    <Box>
                      <Typography variant="body2" fontWeight={500}>
                        {tpl.title}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'normal' }}>
                        {tpl.description}
                      </Typography>
                    </Box>
                  </MenuItem>
                ))}
              </Menu>
            </Box>
            {plans.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ px: 2, py: 2 }}>
                {t('aiChat.planEmpty')}
              </Typography>
            ) : (
              <List dense disablePadding>
                {plans.map((p) => (
                  <ListItemButton key={p.id} onClick={() => openDetail(p.id)} divider>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                          <Typography variant="body2" fontWeight={500} noWrap sx={{ flexGrow: 1 }}>
                            {p.title}
                          </Typography>
                          {p.status === 'completed' && (
                            <Chip
                              size="small"
                              color="success"
                              label={t('aiChat.planCompletedChip')}
                              sx={{ height: 18, fontSize: 10 }}
                            />
                          )}
                        </Box>
                      }
                      secondary={
                        <Box component="span" sx={{ display: 'block', mt: 0.5 }}>
                          <LinearProgress
                            variant="determinate"
                            value={p.taskCount > 0 ? (p.doneCount / p.taskCount) * 100 : 0}
                            sx={{ height: 4, borderRadius: 2, mb: 0.25 }}
                          />
                          <Typography component="span" variant="caption" color="text.secondary">
                            {t('aiChat.planProgress', { done: p.doneCount, total: p.taskCount })}
                          </Typography>
                        </Box>
                      }
                      secondaryTypographyProps={{ component: 'div' }}
                    />
                  </ListItemButton>
                ))}
              </List>
            )}
          </>
        ) : view === 'detail' && plan ? (
          /* ------- Plan detayı ------- */
          <>
            {!agentEnabled && (
              <Alert severity="info" sx={{ m: 1, fontSize: 12 }}>
                {t('aiChat.planAgentRequired')}
              </Alert>
            )}
            {/* Bağlam değişkenleri */}
            {Object.keys(plan.contextVars).length > 0 && (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, px: 1, pt: 1 }}>
                {Object.entries(plan.contextVars).map(([k, v]) => (
                  <Chip key={k} size="small" label={`${k}: ${v}`} sx={{ height: 20, fontSize: 11 }} />
                ))}
              </Box>
            )}
            <Box sx={{ px: 1, pt: 1, pb: 0.5 }}>
              <LinearProgress
                variant="determinate"
                value={
                  orderedTasks.length > 0
                    ? (orderedTasks.filter((task) => task.status === 'done').length / orderedTasks.length) * 100
                    : 0
                }
                sx={{ height: 5, borderRadius: 3 }}
              />
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 0.5 }}>
                <Typography variant="caption" color="text.secondary">
                  {t('aiChat.planProgress', {
                    done: orderedTasks.filter((task) => task.status === 'done').length,
                    total: orderedTasks.length,
                  })}
                </Typography>
                {/* "Tümünü yap": done olmayan görev varsa. Yalnızca sıradaki görev
                    ELLE TAMAMLANAN bir needsReview ise gizlenir (kullanıcı kararı şart);
                    durdurma sonucu needsReview kalan normal görevler akışla sürdürülebilir. */}
                {(() => {
                  const next = orderedTasks.find((task) => task.status !== 'done');
                  if (!next || (next.status === 'needsReview' && isManualCompletionTask(next))) return null;
                  const canRunAll = agentEnabled && !busy && activeTaskId === null;
                  return (
                    <Tooltip title={!agentEnabled ? t('aiChat.planAgentRequired') : ''}>
                      <span>
                        <Button
                          size="small"
                          variant="contained"
                          color="secondary"
                          startIcon={<PlaylistPlayIcon sx={{ fontSize: 16 }} />}
                          disabled={!canRunAll}
                          onClick={() => setRunAllConfirmOpen(true)}
                          sx={{ fontSize: 11, py: 0.25, px: 1, minWidth: 0, whiteSpace: 'nowrap' }}
                        >
                          {t('aiChat.planRunAll')}
                        </Button>
                      </span>
                    </Tooltip>
                  );
                })()}
              </Box>
            </Box>
            <Divider />
            {orderedTasks.map((task, i) => renderTaskRow(task, i))}
          </>
        ) : view === 'new' ? (
          /* ------- Yeni plan formu ------- */
          <Box sx={{ p: 1.5, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <TextField
              size="small"
              fullWidth
              label={t('aiChat.planFormName')}
              value={newTitle}
              onChange={(e) => setNewTitle(e.target.value)}
              inputProps={{ maxLength: 200 }}
            />

            {/* Değişkenler ({degisken} yer tutucularını doldurur) */}
            <Box>
              <Typography variant="caption" fontWeight={600} color="text.secondary">
                {t('aiChat.planFormVars')}
              </Typography>
              {newVars.map((v, i) => (
                <Box key={i} sx={{ display: 'flex', gap: 0.75, mt: 0.75 }}>
                  <TextField
                    size="small"
                    placeholder={t('aiChat.planFormVarName')}
                    value={v.key}
                    // Şablon değişkeninin anahtarı görev komutlarındaki {yerTutucu} ile
                    // eşleşmek zorunda — şablondan gelince değiştirilemez
                    disabled={!!v.label}
                    onChange={(e) =>
                      setNewVars((prev) => prev.map((x, j) => (j === i ? { ...x, key: e.target.value } : x)))
                    }
                    sx={{ flex: 1 }}
                  />
                  <TextField
                    size="small"
                    label={v.label || undefined}
                    required={v.required === true}
                    placeholder={t('aiChat.planFormVarValue')}
                    value={v.value}
                    onChange={(e) =>
                      setNewVars((prev) => prev.map((x, j) => (j === i ? { ...x, value: e.target.value } : x)))
                    }
                    sx={{ flex: 1.4 }}
                  />
                  <IconButton
                    size="small"
                    disabled={v.required === true}
                    onClick={() => setNewVars((prev) => prev.filter((_, j) => j !== i))}
                  >
                    <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Box>
              ))}
              <Button
                size="small"
                startIcon={<AddIcon />}
                onClick={() => setNewVars((prev) => [...prev, { key: '', value: '' }])}
                sx={{ mt: 0.5, fontSize: 11 }}
              >
                {t('aiChat.planFormAddVar')}
              </Button>
            </Box>

            {/* Görevler */}
            <Box>
              <Typography variant="caption" fontWeight={600} color="text.secondary">
                {t('aiChat.planFormTasks')}
              </Typography>
              {newTasks.map((task, i) => (
                <Paper key={i} variant="outlined" sx={{ p: 1, mt: 0.75, borderRadius: 1.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.75 }}>
                    <Typography variant="caption" fontWeight={600} sx={{ flexGrow: 1 }}>
                      {i + 1}.
                    </Typography>
                    <IconButton size="small" disabled={i === 0} onClick={() => moveTask(i, -1)}>
                      <ArrowUpwardIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                    <IconButton size="small" disabled={i === newTasks.length - 1} onClick={() => moveTask(i, 1)}>
                      <ArrowDownwardIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                    <IconButton
                      size="small"
                      disabled={newTasks.length <= 1}
                      onClick={() => setNewTasks((prev) => prev.filter((_, j) => j !== i))}
                    >
                      <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Box>
                  <TextField
                    size="small"
                    fullWidth
                    placeholder={t('aiChat.planFormTaskTitle')}
                    value={task.title}
                    onChange={(e) =>
                      setNewTasks((prev) => prev.map((x, j) => (j === i ? { ...x, title: e.target.value } : x)))
                    }
                    inputProps={{ maxLength: 300 }}
                    sx={{ mb: 0.75 }}
                  />
                  <TextField
                    size="small"
                    fullWidth
                    multiline
                    minRows={2}
                    placeholder={t('aiChat.planFormTaskPrompt')}
                    value={task.promptTemplate}
                    onChange={(e) =>
                      setNewTasks((prev) =>
                        prev.map((x, j) => (j === i ? { ...x, promptTemplate: e.target.value } : x))
                      )
                    }
                  />
                </Paper>
              ))}
              <Button
                size="small"
                startIcon={<AddIcon />}
                onClick={() => setNewTasks((prev) => [...prev, { title: '', promptTemplate: '' }])}
                sx={{ mt: 0.75, fontSize: 11 }}
              >
                {t('aiChat.planFormAddTask')}
              </Button>
            </Box>

            <Button
              variant="contained"
              disabled={!canCreate || creating}
              onClick={handleCreate}
              startIcon={creating ? <CircularProgress size={14} color="inherit" /> : <CheckCircleIcon />}
            >
              {t('aiChat.planFormCreate')}
            </Button>
          </Box>
        ) : null}
      </Box>

      {/* Elle görev durumu değiştirme menüsü */}
      <Menu
        anchorEl={taskMenu?.anchor ?? null}
        open={Boolean(taskMenu)}
        onClose={() => setTaskMenu(null)}
        sx={{ zIndex: 2300 }}
      >
        <Typography variant="caption" sx={{ px: 2, py: 0.5, display: 'block', color: 'text.disabled', fontWeight: 700 }}>
          {t('aiChat.planSetStatus')}
        </Typography>
        <MenuItem sx={{ fontSize: 13 }} onClick={() => taskMenu && handleSetTaskStatus(taskMenu.taskId, 'pending')}>
          <RadioButtonUncheckedIcon sx={{ fontSize: 16, mr: 1, color: 'text.disabled' }} />
          {t('aiChat.planStatusPending')}
        </MenuItem>
        <MenuItem sx={{ fontSize: 13 }} onClick={() => taskMenu && handleSetTaskStatus(taskMenu.taskId, 'done')}>
          <CheckCircleIcon sx={{ fontSize: 16, mr: 1, color: 'success.main' }} />
          {t('aiChat.planStatusDone')}
        </MenuItem>
        <MenuItem sx={{ fontSize: 13 }} onClick={() => taskMenu && handleSetTaskStatus(taskMenu.taskId, 'failed')}>
          <ErrorOutlineIcon sx={{ fontSize: 16, mr: 1, color: 'error.main' }} />
          {t('aiChat.planStatusFailed')}
        </MenuItem>
        <MenuItem sx={{ fontSize: 13 }} onClick={() => taskMenu && handleSetTaskStatus(taskMenu.taskId, 'needsReview')}>
          <HelpOutlineIcon sx={{ fontSize: 16, mr: 1, color: 'warning.main' }} />
          {t('aiChat.planStatusNeedsReview')}
        </MenuItem>
      </Menu>

      {/* "Tümünü yap" onay modalı */}
      <Dialog
        open={runAllConfirmOpen}
        onClose={() => setRunAllConfirmOpen(false)}
        sx={{ zIndex: 2400 }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, fontSize: 17 }}>
          <PlaylistPlayIcon color="secondary" />
          {t('aiChat.planRunAll')}
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ fontSize: 14 }}>
            {t('aiChat.planRunAllConfirm')}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setRunAllConfirmOpen(false)} color="inherit">
            {t('aiChat.planRunAllCancel')}
          </Button>
          <Button
            variant="contained"
            color="secondary"
            startIcon={<PlaylistPlayIcon />}
            onClick={() => {
              setRunAllConfirmOpen(false);
              if (plan) onRunAll(plan);
            }}
          >
            {t('aiChat.planRunAllStart')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Plan silme onay modalı */}
      <Dialog
        open={deleteConfirmId !== null}
        onClose={() => setDeleteConfirmId(null)}
        sx={{ zIndex: 2400 }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, fontSize: 17 }}>
          <DeleteOutlineIcon color="error" />
          {t('aiChat.planDelete')}
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ fontSize: 14 }}>
            {t('aiChat.planDeleteConfirm')}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeleteConfirmId(null)} color="inherit">
            {t('aiChat.planRunAllCancel')}
          </Button>
          <Button
            variant="contained"
            color="error"
            startIcon={<DeleteOutlineIcon />}
            onClick={confirmDeletePlan}
          >
            {t('aiChat.planDelete')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dışa aktarma adı modalı: seçilen ad hem plan adı hem dosya adı olur */}
      <Dialog
        open={exportDialog !== null}
        onClose={() => setExportDialog(null)}
        sx={{ zIndex: 2400 }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, fontSize: 17 }}>
          <FileDownloadIcon color="primary" />
          {t('aiChat.planExportTitle')}
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ fontSize: 13, mb: 1.5 }}>
            {t('aiChat.planExportNameHint')}
          </DialogContentText>
          <TextField
            autoFocus
            fullWidth
            size="small"
            label={t('aiChat.planExportNameLabel')}
            value={exportDialog?.title ?? ''}
            onChange={(e) =>
              setExportDialog((cur) => (cur ? { ...cur, title: e.target.value } : cur))
            }
            onKeyDown={(e) => {
              if (e.key === 'Enter' && exportDialog?.title.trim()) confirmExportPlan();
            }}
            inputProps={{ maxLength: 100 }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setExportDialog(null)} color="inherit">
            {t('aiChat.planRunAllCancel')}
          </Button>
          <Button
            variant="contained"
            startIcon={<FileDownloadIcon />}
            disabled={!exportDialog?.title.trim()}
            onClick={confirmExportPlan}
          >
            {t('aiChat.planExportTitle')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );

  // Mobil: tam ekran Dialog; masaüstü: chat Paper'ının soluna sabit kardeş Paper
  if (isMobile) {
    return (
      <Dialog open fullScreen onClose={onClose} sx={{ zIndex: 2200 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>{content}</Box>
      </Dialog>
    );
  }

  return (
    <Paper
      elevation={8}
      sx={{
        position: 'fixed',
        // Chat Paper'ı right:16 + dinamik genişlik — panel onun soluna 8px boşlukla
        right: 16 + chatWidth + 8,
        bottom: 88,
        width: panelWidth,
        height: 'auto',
        minHeight: 560,
        maxHeight: 'calc(100vh - 120px)',
        zIndex: 2100,
        display: 'flex',
        flexDirection: 'column',
        borderRadius: 3,
        overflow: 'hidden',
        // Sürükleme sırasında metin seçimini ve geçiş animasyonunu kapat
        userSelect: resizing ? 'none' : undefined,
        transition: resizing ? 'none' : 'width 0.15s ease',
      }}
    >
      {/* Sol kenar: sürükleyerek genişletme tutamacı */}
      <Box
        onPointerDown={handleResizeStart}
        sx={{
          position: 'absolute',
          left: 0,
          top: 0,
          bottom: 0,
          width: 8,
          cursor: 'ew-resize',
          zIndex: 1,
          '&::after': {
            content: '""',
            position: 'absolute',
            left: 0,
            top: 0,
            bottom: 0,
            width: 3,
            bgcolor: resizing ? 'primary.main' : 'transparent',
            transition: 'background-color 0.15s',
          },
          '&:hover::after': { bgcolor: 'primary.main' },
        }}
      />
      {content}
    </Paper>
  );
};

export default AiPlanPanel;
